import React from 'react';
import i18n from '../i18n';

// Components
import { Typography } from '../components/display/Typography';

// Utils
import { OBJECT_ID_SEGMENT } from '../utils/breadcrumbs';
import { PendingCrumb } from '../components/navigation/Breadcrumbs/PendingCrumb';

export type RouteUIElement = {
  content: React.ReactNode;
  path: string;
  pathAbsolute: string;
};

// Función que genera las etiquetas de rutas dinámicamente
export const getLabelToRoutes = (): { [key: string]: string } => ({
  dashboard: i18n.t('navigation:routes.dashboard'),
  communication: i18n.t('navigation:routes.communication'),
  communications: i18n.t('navigation:routes.communications'),
  'duplicate-communication': i18n.t(
    'navigation:routes.duplicate-communication'
  ),
  'search-create-historical-communication': i18n.t(
    'navigation:routes.search-create-historical-communication'
  ),
  'work-orders': i18n.t('navigation:routes.work-orders'),
  'new-order': i18n.t('navigation:routes.new-order'),
  'duplicate-order': i18n.t('navigation:routes.duplicate-order'),
  'new-order-duplicate': i18n.t('navigation:routes.new-order-duplicate'),
  'search-create-historical-order': i18n.t(
    'navigation:routes.search-create-historical-order'
  ),
  multimedia: i18n.t('navigation:routes.multimedia'),
  // Pestaña "Multimedia" (listado tabla) de los LISTADOS de módulo —
  // `/work-orders/campaign/multimedia-list` y
  // `/surveillance-route/multimedia-list`. Reusa la MISMA key i18n que
  // `multimedia` (el grid del contenedor), no crea key nueva — ambas se leen
  // "Multimedia" en las 3 locales.
  'multimedia-list': i18n.t('navigation:routes.multimedia'),
  summary: i18n.t('navigation:routes.summary'),
  'new-communication': i18n.t('navigation:routes.new-communication'),
  'new-communication-duplicate': i18n.t(
    'navigation:routes.new-communication-duplicate'
  ),
  campaign: i18n.t('navigation:routes.campaign'),
  'search-create-historical-campaign': i18n.t(
    'navigation:routes.search-create-historical-campaign'
  ),
  'new-campaign': i18n.t('navigation:routes.new-campaign'),
  'edit-campaign': i18n.t('navigation:routes.edit-campaign'),
  'edit-communication': i18n.t('navigation:routes.edit-communication'),
  'general-view-route': i18n.t('navigation:routes.general-view-route'),
  'surveillance-route': i18n.t('navigation:routes.surveillance-route'),
  'new-route': i18n.t('navigation:routes.new-route'),
  'child-orders': i18n.t('navigation:routes.child-orders'),
  // Tour P2 (2026-07-06): el breadcrumb reutiliza la MISMA key que el ítem de
  // menú ("Recorridos Históricos") — aquí no se "crea" nada (FR-2.6), solo se
  // consulta/descarga; el texto viejo "Buscar y crear históricos" contradecía
  // el spec y el nombre del menú.
  'search-create-historical-route': i18n.t('pages:reporting.historicalRoutes'),
  // F5 (2026-07-17): el wizard EditRoute vive en `/surveillance-route/:routeId/edit`
  // — el path segment literal es `edit` (no `edit-route`, a diferencia de
  // `edit-campaign`/`edit-accident`/`edit-communication`, que SÍ llevan el
  // prefijo de entidad en su propia ruta). Sin esta entrada, `getRouteLabel`
  // caía al fallback `labels[key] || key` y el breadcrumb mostraba el
  // segmento crudo "edit".
  edit: i18n.t('navigation:routes.edit-route'),
  reporting: i18n.t('navigation:routes.reporting'),
  accidents: i18n.t('navigation:routes.accidents'),
  'edit-accident': i18n.t('navigation:routes.edit-accident'),
  accident: i18n.t('navigation:routes.accident'),
  'damages-and-vehicles': i18n.t('navigation:routes.damages-and-vehicles'),
  accidentSummary: i18n.t('navigation:routes.accidentSummary'),
  actions: i18n.t('navigation:routes.actions'),
  // Como en el menú lateral: la página de validación NO es la pestaña
  // Actuaciones, y con el mismo nombre no había forma de distinguirlas
  // (spec 2026-10-06-return-to-origin § 4 E3).
  'actions-validation': i18n.t('pages:workOrders.actionsValidation'),
  'new-action': i18n.t('navigation:routes.new-action'),
  'new-accident': i18n.t('navigation:routes.new-accident'),
  'basic-info': i18n.t('navigation:routes.basic-info'),
  details: i18n.t('navigation:routes.details'),
  detail: i18n.t('navigation:routes.detail'),
  'operators-and-vehicles': i18n.t('navigation:routes.operators-and-vehicles'),
  location: i18n.t('navigation:routes.location'),
  edition: i18n.t('navigation:routes.edition'),
  materials: i18n.t('navigation:routes.materials'),
  machinery: i18n.t('navigation:routes.machinery'),
  animals: i18n.t('navigation:routes.animals'),
  management: i18n.t('navigation:routes.management'),
  users: i18n.t('navigation:routes.users'),
  vehicles: i18n.t('navigation:routes.vehicles'),
  teams: i18n.t('navigation:routes.teams'),
  groups: i18n.t('navigation:routes.groups'),
  'segment-review': i18n.t('navigation:routes.segment-review'),
  'auxiliar-machinery': i18n.t('navigation:routes.auxiliar-machinery'),
  'external-companies': i18n.t('navigation:routes.external-companies'),
  'route-presets': i18n.t('navigation:routes.route-presets'),
  'budget-categories': i18n.t('navigation:routes.budget-categories'),
  'budget-items': i18n.t('navigation:routes.budget-items'),
  budgets: i18n.t('navigation:routes.budgets'),
  'search-and-create-historical': i18n.t(
    'navigation:routes.search-and-create-historical'
  ),
  'historical-actions': i18n.t('navigation:routes.historical-actions'),
  'historical-accidents': i18n.t('navigation:routes.historical-accidents'),
  'time-management': i18n.t('navigation:routes.time-management'),
  'historical-generic-orders': i18n.t(
    'navigation:routes.historical-generic-orders'
  ),
  'historical-campaigns': i18n.t('navigation:routes.historical-campaigns'),
  'historical-communications': i18n.t(
    'navigation:routes.historical-communications'
  ),
  'historical-budgets': i18n.t('navigation:routes.historical-budgets'),
  admin: i18n.t('navigation:routes.admin'),
  tenants: i18n.t('navigation:routes.tenants'),
});

// Función auxiliar para obtener una etiqueta específica
export const getRouteLabel = (key: string): string => {
  const labels = getLabelToRoutes();
  return labels[key] || key;
};

// `getRouteLabel` devuelve la propia clave cuando no hay etiqueta, así que un
// segmento de id se pintaba con el hash de Mongo en crudo hasta que el detalle
// cargaba y `Breadcrumbs` lo sustituía por el ROID. El porqué de la marca de
// posición está en `utils/breadcrumbs.ts`.
export const createRoutes = (routes: string[], data: RouteUIElement[]) =>
  routes.reduce((accumulator, current, index) => {
    accumulator.push({
      content: OBJECT_ID_SEGMENT.test(current) ? (
        <PendingCrumb />
      ) : (
        <Typography size={'S'} colorText={'greyDark'}>
          {getRouteLabel(current)}
        </Typography>
      ),
      path: current,
      // V4 iter7+ (2026-05-07) — fix breadcrumb URLs en rutas profundas.
      // Antes el reducer concatenaba `accumulator[index].path` (solo el
      // segmento previo) en lugar de `accumulator[index].pathAbsolute`
      // (la URL acumulada). Para profundidad ≥3 (ej.
      // `/surveillance-route/:id/new-action/details/edition`) producía URLs
      // como `/<id>/new-action`, `/details/edition` (rotas, sin prefijo).
      // Cada `OrderType` case del Breadcrumbs sobreescribía data[N]
      // .pathAbsolute para taparlo, pero solo cuando había `roidOTSelected`
      // dispatched. Este fix corrige la causa raíz.
      pathAbsolute:
        index > 0
          ? accumulator[index].pathAbsolute + '/' + current
          : accumulator[index].path + current,
    });
    return accumulator;
  }, data);
